import { isOutreachRole } from "./roles";
import { isStaffEmail } from "./staffDomain";
import {
  ADMIN_ROLES,
  ADMIN_TIER_ROLES,
  ACCOUNTING_ROLES,
  OUTREACH_ROLES,
  INVESTOR_DASHBOARD_ROLES,
  GLOBAL_ROLES,
  type GlobalRole,
} from "./roles";

export {
  ADMIN_ROLES,
  ADMIN_TIER_ROLES,
  ACCOUNTING_ROLES,
  OUTREACH_ROLES,
  INVESTOR_DASHBOARD_ROLES,
};

export const SUPER_ADMIN_ONLY = ["super_admin"] as const;

export function isStaffAccount(
  role: string | undefined,
  email: string | undefined,
): boolean {
  return isOutreachRole(role) || isStaffEmail(email);
}

export type PermissionResult<T = void> =
  | { success: true; data: T }
  | { success: false; error: string };

export function hasRole(
  userRole: GlobalRole | undefined,
  allowedRoles: readonly GlobalRole[],
): boolean {
  return !!userRole && allowedRoles.includes(userRole);
}

export function isAdmin(userRole: GlobalRole | undefined): boolean {
  return hasRole(userRole, ADMIN_ROLES);
}

export function isOutreach(userRole: GlobalRole | undefined): boolean {
  return hasRole(userRole, OUTREACH_ROLES);
}

export function isInvestorDashboard(
  userRole: GlobalRole | undefined,
): boolean {
  return hasRole(userRole, INVESTOR_DASHBOARD_ROLES);
}

export function isAccounting(userRole: GlobalRole | undefined): boolean {
  return hasRole(userRole, ACCOUNTING_ROLES);
}

export function isAdminTier(userRole: GlobalRole | undefined): boolean {
  return hasRole(userRole, ADMIN_TIER_ROLES);
}

export function isSuperAdmin(userRole: GlobalRole | undefined): boolean {
  return hasRole(userRole, SUPER_ADMIN_ONLY);
}

export function requireRole(
  userRole: GlobalRole | undefined,
  allowedRoles: readonly GlobalRole[],
): void {
  if (!hasRole(userRole, allowedRoles)) {
    throw new Error(
      `Unauthorized: Requires one of these roles: ${allowedRoles.join(", ")}`,
    );
  }
}

export function requireAdmin(userRole: GlobalRole | undefined): void {
  requireRole(userRole, ADMIN_ROLES);
}

export function requireSuperAdmin(userRole: GlobalRole | undefined): void {
  requireRole(userRole, SUPER_ADMIN_ONLY);
}

export function hasEqualOrHigherPrivilege(
  role1: GlobalRole,
  role2: GlobalRole,
): boolean {
  const roleHierarchy = new Map(
    GLOBAL_ROLES.map((role, index) => [role, index]),
  );

  return (
    (roleHierarchy.get(role1) ?? Infinity) <= (roleHierarchy.get(role2) ?? Infinity)
  );
}

export function requireMinimumRole(
  userRole: GlobalRole | undefined,
  minimumRole: GlobalRole,
): void {
  if (!userRole || !hasEqualOrHigherPrivilege(userRole, minimumRole)) {
    throw new Error(`Unauthorized: Requires minimum role: ${minimumRole}`);
  }
}
